import { FC, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";

import { VideoTopicResource } from "@/types/resources";
import { TopicMaRating } from "@/utils/enums";
import Tag from "../Tag";
import { hexToRgba } from "@/utils/theme";
import Tooltip from "./Tooltip";

const Dot = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
`;

interface Props {
  topic: VideoTopicResource;
}

const TopicTag: FC<Props> = ({ topic }) => {
  const theme = useTheme();

  const [showTooltip, setShowTooltip] = useState(true);
  const [domRect, setDomRect] = useState<DOMRect>();

  const ref = useRef<HTMLSpanElement>(null);

  const getDotColor = () => {
    switch (topic.ma_rating) {
      case TopicMaRating.BAD:
        return theme.colors.warning;
      case TopicMaRating.VERY_BAD:
        return hexToRgba(theme.colors.warning, 0.3);
      case TopicMaRating.GOOD:
        return hexToRgba(theme.colors.success, 0.3);
      case TopicMaRating.VERY_GOOD:
        return theme.colors.success;
      default:
        return null;
    }
  };

  const getTooltipText = () => {
    switch (topic.ma_rating) {
      case TopicMaRating.BAD:
        return "Bad performance";
      case TopicMaRating.VERY_BAD:
        return "Very bad performance";
      case TopicMaRating.GOOD:
        return "Good performance";
      case TopicMaRating.VERY_GOOD:
        return "Very good performance";
      default:
        return "N/A";
    }
  };

  const dotColor = getDotColor();

  return (
    <span
      onMouseEnter={() => {
        setShowTooltip(true);
        setDomRect(ref.current?.getBoundingClientRect());
      }}
      onMouseLeave={() => setShowTooltip(false)}
      ref={ref}
    >
      <Tag>
        {!!dotColor && <Dot $color={dotColor} />}
        {topic.title}
      </Tag>
      {showTooltip && !!dotColor && (
        <Tooltip
          domRect={domRect}
          text={getTooltipText()}
          color={dotColor}
          hideTooltip={() => setShowTooltip(false)}
        />
      )}
    </span>
  );
};

export default TopicTag;
